/**
 * Dialog for confirming team deletion with warning about cascading effects
 * on team members, module access, and dashboard configurations.
 */
import { AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";


interface Team {
	id: string;
	name: string;
}

interface DeleteTeamDialogProps {
	team: Team;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess: () => void;
}

/**
 * Dialog component for confirming team deletion
 * @param props - Component props
 * @param props.team - The team to delete
 * @param props.open - Whether the dialog is open
 * @param props.onOpenChange - Callback when dialog open state changes
 * @param props.onSuccess - Callback when team is deleted successfully
 */
export function DeleteTeamDialog({
	team,
	open,
	onOpenChange,
	onSuccess,
}: DeleteTeamDialogProps) {
	// Delete team mutation
	const deleteMutation = trpc.teams.delete.useMutation({
		onSuccess: () => {
			onSuccess();
		},
	});

	/** Executes the team deletion */
	const handleDelete = () => {
		deleteMutation.mutate({ teamId: team.id });
	};

	/**
	 * Handles dialog open state change and resets mutation on close
	 * @param newOpen - New dialog open state
	 */
	const handleOpenChange = (newOpen: boolean) => {
		if (!newOpen) {
			deleteMutation.reset();
		}
		onOpenChange(newOpen);
	};

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<DialogContent>
				<form onSubmit={(e) => { e.preventDefault(); handleDelete(); }}>
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<AlertTriangle className="h-5 w-5 text-red-400" />
							Delete Team
						</DialogTitle>
						<DialogDescription>
							Are you sure you want to delete{" "}
							<span className="font-medium text-foreground">{team.name}</span>?
						</DialogDescription>
					</DialogHeader>
					<div className="py-4">
						<div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm">
							<p className="font-medium text-red-400 mb-2">
								This action cannot be undone.
							</p>
							<p className="text-muted-foreground">
								Deleting this team will also remove:
							</p>
							<ul className="list-disc list-inside mt-1 text-muted-foreground">
								<li>All team member assignments</li>
								<li>Team module access configurations</li>
								<li>Team dashboard configurations</li>
							</ul>
						</div>

						{/* Error */}
						{deleteMutation.isError && (
							<div className="rounded-md bg-destructive/10 p-3 text-sm text-red-400 mt-4">
								{deleteMutation.error.message}
							</div>
						)}
					</div>
					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => { handleOpenChange(false); }}
						>
							Cancel
						</Button>
						<Button
							type="submit"
							variant="destructive"
							disabled={deleteMutation.isPending}
						>
							{deleteMutation.isPending ? "Deleting..." : "Delete Team"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
